import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, unwrap } from '../api/client.js';
import { errorMessage } from '../errors.js';
import { formatDateTime } from '../i18n.js';
import { Button, Notice } from './ui.js';

export interface SiteAccess {
  readonly block: 'bot_protection' | 'auth_required' | null;
  readonly checkerIp: string | null;
  readonly userAgent: string;
}

function CopyValue({ value }: { value: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  return (
    <span className="flex flex-wrap items-center gap-2">
      <code className="rounded border border-slate-300 bg-slate-50 px-2 py-1 font-mono text-sm break-all">
        {value}
      </code>
      <Button
        type="button"
        variant="secondary"
        className="min-h-9 px-3 py-1 text-sm"
        onClick={() => {
          navigator.clipboard
            ?.writeText(value)
            .then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            })
            .catch(() => undefined);
        }}
      >
        {copied ? t('setup.copied') : t('setup.copy')}
      </Button>
    </span>
  );
}

/**
 * S3 when a bot protection or a password hides the site (T-34): not "offline", but what the customer
 * can do, step by step, and what becomes visible afterwards.
 */
export function SetupPanel({
  siteId,
  access,
  lastCheckAt,
  timeZone,
}: {
  siteId: string;
  access: SiteAccess;
  lastCheckAt: string | null;
  timeZone?: string | undefined;
}) {
  const { t, i18n } = useTranslation();
  const client = useQueryClient();
  const recheck = useMutation({
    mutationFn: () => unwrap(api.POST('/sites/{siteId}/recheck', { params: { path: { siteId } } })),
    onSuccess: () => {
      // The check runs within seconds; the dashboard refreshes itself every 30 s, sooner here.
      setTimeout(() => void client.invalidateQueries({ queryKey: ['site', siteId] }), 15_000);
    },
  });
  if (!access.block) return null;

  if (access.block === 'auth_required')
    return (
      <Notice kind="info">
        <p className="font-semibold">{t('setup.auth.title')}</p>
        <p>{t('setup.auth.text')}</p>
      </Notice>
    );

  return (
    <section
      aria-labelledby="setup-title"
      className="flex flex-col gap-4 rounded-md border border-violet-200 bg-violet-50 p-4"
    >
      <div className="flex flex-col gap-1">
        <h2 id="setup-title" className="text-xl font-semibold text-violet-900">
          {t('setup.bot.title')}
        </h2>
        <p>{t('setup.bot.text')}</p>
      </div>
      <ol className="flex flex-col gap-3">
        <li className="rounded-md border border-slate-200 bg-white p-3">
          <p className="font-semibold">{t('setup.bot.step1')}</p>
          <p className="text-slate-700">{t('setup.bot.step1Text')}</p>
        </li>
        {access.checkerIp ? (
          <li className="flex flex-col gap-2 rounded-md border border-slate-200 bg-white p-3">
            <p className="font-semibold">{t('setup.bot.step2')}</p>
            <p className="text-slate-700">{t('setup.bot.step2Text')}</p>
            <CopyValue value={access.checkerIp} />
          </li>
        ) : null}
        <li className="flex flex-col gap-2 rounded-md border border-slate-200 bg-white p-3">
          <p className="font-semibold">{t('setup.bot.step3')}</p>
          <p className="text-slate-700">{t('setup.bot.step3Text')}</p>
          <CopyValue value={access.userAgent} />
        </li>
        <li className="flex flex-col gap-2 rounded-md border border-slate-200 bg-white p-3">
          <p className="font-semibold">{t('setup.bot.step4')}</p>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              busy={recheck.isPending}
              disabled={recheck.isSuccess}
              onClick={() => recheck.mutate()}
            >
              {t('setup.recheck')}
            </Button>
            {lastCheckAt ? (
              <span className="text-sm text-slate-700">
                {t('setup.lastAttempt', {
                  time: formatDateTime(lastCheckAt, i18n.language, timeZone),
                })}
              </span>
            ) : null}
          </div>
          {recheck.isSuccess ? <p role="status">{t('setup.recheckQueued')}</p> : null}
          {recheck.isError ? <p role="alert">{errorMessage(recheck.error, t)}</p> : null}
        </li>
      </ol>
      <div>
        <p className="font-semibold">{t('setup.unlockTitle')}</p>
        <p className="text-slate-700">{t('setup.unlockText')}</p>
      </div>
    </section>
  );
}
